// The 2-step re-check and reason for sensitive staff actions (console prototype 82-governance.js guarded()).
// The server decides (app.staff_guard: a reason of 8+ characters and a 2FA from the last 10 minutes); this dialog
// asks for both, refreshes the 2FA with Supabase's challenge/verify, then runs the action with the reason.
import { t } from '@xr/i18n';
import { useState } from 'react';
import { plain } from '../lib/errors';
import { L, prefs } from '../lib/prefs';
import { DbError, sb } from '../lib/supabase';
import { Btn, Field } from './kit';
import { closeModal, openModal } from './modal';
import { toast } from './toast';

export const reasonOk = (s: string) => s.trim().length >= 8;
export const codeOk = (s: string) => /^\d{6}$/.test(s.trim());

function GuardForm({ run, onErr }: { run: (reason: string) => Promise<void>; onErr?: (e: unknown) => void }) {
  const [code, setCode] = useState('');
  const [why, setWhy] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const ok = codeOk(code) && reasonOk(why);
  const go = async () => {
    if (!ok) return;
    setBusy(true); setErr('');
    try {
      const f = await sb.auth.mfa.listFactors();
      const totp = f.data?.totp.find(x => x.status === 'verified');
      if (!totp) { setErr(L('Set up an authenticator in Account › Security first.', 'أعدّ تطبيق المصادقة في الحساب › الأمان أولاً.')); setBusy(false); return; }
      const v = await sb.auth.mfa.challengeAndVerify({ factorId: totp.id, code: code.trim() });
      if (v.error) { setErr(t(prefs.get().lang, 'otpWrong')); setBusy(false); return; }
      await run(why.trim());
      closeModal();
    } catch (e) {
      if (e instanceof DbError && e.code === 'mfa_recheck') toast(L('The check ran out. Enter a new code.', 'انتهت صلاحية التحقق. أدخل رمزاً جديداً.'), { tone: 'bad' });
      else if (onErr) onErr(e);
      else toast(plain(e), { tone: 'bad' });
      setBusy(false);
    }
  };
  return (
    <>
      <div className="m-body">
        <Field label={L('Code from your authenticator', 'الرمز من تطبيق المصادقة')} htmlFor="gd-c" err={err || undefined}>
          <input className="inp" id="gd-c" inputMode="numeric" autoComplete="one-time-code" maxLength={6} dir="ltr" placeholder="000000" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} data-autofocus />
        </Field>
        <Field label={L('Reason', 'السبب')} htmlFor="gd-w" hint={L('At least a few words. Shown to other super admins.', 'بضع كلمات على الأقل.')}>
          <textarea className="inp" id="gd-w" rows={2} value={why} onChange={e => setWhy(e.target.value)} />
        </Field>
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" disabled={!ok} why={L('Enter the 6-digit code and a reason', 'أدخل الرمز والسبب')} busy={busy} onClick={() => void go()}>{L('Confirm', 'تأكيد')}</Btn>
      </footer>
    </>
  );
}

/** Asks for the 2FA code and a reason, then runs the action with the reason. */
export function guarded(title: string, run: (reason: string) => Promise<void>, onErr?: (e: unknown) => void) {
  openModal({ title, sub: L('A sensitive area: confirm it\'s you and say why. Both go in the activity log.', 'منطقة حساسة: أكد هويتك واذكر السبب. يُسجل كلاهما.'), size: 'sm', bare: true, body: <GuardForm run={run} onErr={onErr} /> });
}
